/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';

interface SentFileMetadata {
  filename?: string;
  name?: string;
  mime_type?: string;
  preview_url?: string;
  url?: string;
  base64?: string;
  size_bytes?: number;
  estimated_size_bytes?: number;
  estimated_payload_bytes?: number;
  base64_length?: number;
  payload_sha256?: string;
  payload_omitted_from_prompt?: boolean;
  stored_attachment_id?: string;
}

interface SentFilesPreviewProps {
  metadata?: {
    files?: SentFileMetadata[];
  } | null;
  isDark?: boolean;
  title?: string;
}

const formatFileSize = (bytes?: number | null): string => {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
    return 'Size unavailable';
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
};

const estimateBase64Bytes = (base64Length?: number): number | null => {
  if (!base64Length || Number.isNaN(base64Length)) {
    return null;
  }
  return Math.round(base64Length * 0.75);
};

const getFileSize = (file: SentFileMetadata): number | null => {
  return (
    file.size_bytes ??
    file.estimated_size_bytes ??
    file.estimated_payload_bytes ??
    estimateBase64Bytes(file.base64_length) ??
    estimateBase64Bytes(file.base64?.length)
  );
};

const getImageSrc = (file: SentFileMetadata): string => {
  if (file.preview_url) {
    return file.preview_url;
  }
  if (file.base64 && file.mime_type) {
    return `data:${file.mime_type};base64,${file.base64}`;
  }
  if (file.url && file.url.startsWith('data:')) {
    return file.url;
  }
  if (file.url && /^https?:\/\//i.test(file.url)) {
    return file.url;
  }
  return '';
};

export default function SentFilesPreview({
  metadata,
  isDark = false,
  title = 'Sent Files',
}: SentFilesPreviewProps) {
  const files = Array.isArray(metadata?.files) ? metadata?.files : [];
  if (!files.length) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title} ({files.length})
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {files.map((file, index) => {
          const filename = file.filename || file.name || `File ${index + 1}`;
          const imageSrc = file.mime_type?.startsWith('image/') ? getImageSrc(file) : '';
          const sizeLabel = formatFileSize(getFileSize(file));
          const hashLabel = file.payload_sha256
            ? `sha256:${file.payload_sha256.slice(0, 10)}`
            : null;

          return (
            <Paper
              key={`${filename}-${index}`}
              variant="outlined"
              sx={{
                p: 1,
                width: 180,
                maxWidth: 220,
                bgcolor: isDark ? 'grey.900' : 'background.paper',
              }}
            >
              <Typography
                variant="caption"
                display="block"
                gutterBottom
                title={filename}
                sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {filename}
              </Typography>

              {imageSrc ? (
                <Box
                  component="img"
                  src={imageSrc}
                  alt={filename}
                  sx={{
                    width: '100%',
                    height: 110,
                    objectFit: 'contain',
                    display: 'block',
                    bgcolor: isDark ? 'grey.800' : 'grey.100',
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    minHeight: 110,
                    bgcolor: isDark ? 'grey.800' : 'grey.100',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body2">
                    {file.mime_type || 'Unknown type'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Preview unavailable
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip size="small" label={sizeLabel} variant="outlined" />
                {file.stored_attachment_id && (
                  <Chip size="small" label="Preview stored" color="success" variant="outlined" />
                )}
                {file.payload_omitted_from_prompt && (
                  <Chip size="small" label="Payload omitted" variant="outlined" />
                )}
              </Box>
              {hashLabel && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  title={file.payload_sha256}
                  sx={{ display: 'block', mt: 0.75, fontFamily: 'monospace' }}
                >
                  {hashLabel}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
